
import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";

import { Form } from "@/components/ui/form";
import { activitySchema, ActivityFormValues } from "./schema/activitySchema";
import { useCreateActivity, useUpdateActivity } from "@/services/activityService";

// Import form sections
import { ActivityBasicInfo } from "./form-sections/ActivityBasicInfo";
import { ActivityTypeSelect } from "./form-sections/ActivityTypeSelect";
import { ActivityParcelSelect } from "./form-sections/ActivityParcelSelect";
import { ActivityDateTimeSelect } from "./form-sections/ActivityDateTimeSelect";
import { ActivityPrioritySelect } from "./form-sections/ActivityPrioritySelect";
import { ActivityStatusSelect } from "./form-sections/ActivityStatusSelect";
import { ActivityReminderSelect } from "./form-sections/ActivityReminderSelect";
import { FormActionButtons } from "./form-sections/FormActionButtons";

interface ActivityFormProps {
  defaultValues?: Partial<ActivityFormValues>;
  onSuccess?: () => void;
  isEditing?: boolean;
}

const ActivityForm: React.FC<ActivityFormProps> = ({ 
  defaultValues, 
  onSuccess,
  isEditing = false
}) => {
  const navigate = useNavigate();
  const createActivity = useCreateActivity();
  const updateActivity = useUpdateActivity();
  
  const form = useForm<ActivityFormValues>({
    resolver: zodResolver(activitySchema),
    defaultValues: {
      title: "",
      description: "",
      activity_type: "irrigation",
      scheduled_for: new Date(),
      time: format(new Date(), "HH:mm"),
      priority: "medium",
      status: "upcoming",
      reminder_time: "1hour",
      ...defaultValues,
    },
  });

  async function onSubmit(data: ActivityFormValues) {
    try {
      // Handle special "none" values
      const formattedData = {
        ...data,
        parcel_id: data.parcel_id === "none" ? undefined : data.parcel_id,
        reminder_time: data.reminder_time === "none" ? undefined : data.reminder_time
      };

      if (isEditing && defaultValues?.id) {
        await updateActivity.mutateAsync({
          id: defaultValues.id as string,
          ...formattedData,
        });
      } else {
        await createActivity.mutateAsync(formattedData);
      }
      
      if (onSuccess) {
        onSuccess();
      } else {
        navigate("/activities");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  }

  const isSubmitting = createActivity.isPending || updateActivity.isPending;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <ActivityBasicInfo form={form} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ActivityTypeSelect form={form} />
          <ActivityParcelSelect form={form} />
          <ActivityDateTimeSelect form={form} />
          <ActivityPrioritySelect form={form} />
          <ActivityStatusSelect form={form} isEditing={isEditing} />
          <ActivityReminderSelect form={form} />
        </div>

        <FormActionButtons 
          isSubmitting={isSubmitting} 
          isEditing={isEditing} 
        />
      </form>
    </Form>
  );
};

export default ActivityForm;
